import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, Sparkles, Leaf, Gem, Moon, AlertCircle, 
  CheckCircle2, Loader2, ChevronDown, ChevronUp, Flame
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";

const WombHealing = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRitual, setExpandedRitual] = useState(null);
  const [expandedHerb, setExpandedHerb] = useState(null);
  const [expandedCrystal, setExpandedCrystal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/womb-healing`);
      setData(res.data);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="womb-healing-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Heart className="w-8 h-8 text-pink-400" />
          Sacred Womb Healing
        </h1>
        <p className="text-mystic-200/70 max-w-3xl">
          {data?.overview?.description}
        </p>
      </motion.div>

      {/* Main Tabs */}
      <Tabs defaultValue="about" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 bg-mystic-800/50 p-1 rounded-xl mb-6 h-auto">
          <TabsTrigger 
            value="about" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg py-2"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Understanding
          </TabsTrigger>
          <TabsTrigger 
            value="rituals" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg py-2"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Rituals
          </TabsTrigger>
          <TabsTrigger 
            value="herbs" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg py-2"
          >
            <Leaf className="w-4 h-4 mr-2" />
            Herbs
          </TabsTrigger>
          <TabsTrigger 
            value="crystals" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg py-2"
          >
            <Gem className="w-4 h-4 mr-2" />
            Crystals
          </TabsTrigger>
        </TabsList>

        {/* Understanding Tab */}
        <TabsContent value="about" className="space-y-6">
          {/* Trauma Types */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" />
                  What Trauma Can the Womb Carry?
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                {data?.trauma_types?.map((trauma, idx) => (
                  <motion.div
                    key={`k-${idx}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="glass-light rounded-xl p-4 space-y-2"
                  >
                    <h4 className="text-mystic-300 font-semibold">{trauma.type}</h4>
                    <p className="text-mystic-100 text-sm">{trauma.description}</p>
                    <p className="text-mystic-200/70 text-xs italic">
                      Impact: {trauma.impact}
                    </p>
                  </motion.div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Signs Healing Needed */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  Signs Your Womb Needs Healing
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-6 md:grid-cols-3">
                {data?.signs_healing_needed?.map((category, idx) => (
                  <div key={`k-${idx}`} className="space-y-3">
                    <h4 className="text-mystic-300 font-semibold flex items-center gap-2">
                      {category.category === "Physical Signs" && <Flame className="w-4 h-4 text-red-400" />}
                      {category.category === "Emotional Signs" && <Heart className="w-4 h-4 text-pink-400" />}
                      {category.category === "Energetic Signs" && <Sparkles className="w-4 h-4 text-purple-400" />}
                      {category.category}
                    </h4>
                    <ul className="space-y-2">
                      {category.signs?.map((sign, sIdx) => (
                        <li key={sIdx} className="text-mystic-200/80 text-sm flex items-start gap-2">
                          <span className="text-mystic-400 mt-1">•</span>
                          {sign}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Benefits */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass bg-gradient-to-br from-pink-500/10 to-purple-500/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  Benefits of Womb Healing
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {data?.healing_benefits?.map((item, idx) => (
                  <motion.div
                    key={`k-${idx}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="glass-light rounded-xl p-4 text-center hover:bg-mystic-700/30 transition-colors"
                  >
                    <h4 className="text-mystic-300 font-medium mb-2">{item.benefit}</h4>
                    <p className="text-mystic-200/70 text-xs">{item.description}</p>
                  </motion.div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Rituals Tab */}
        <TabsContent value="rituals" className="space-y-4">
          {data?.rituals?.map((ritual, idx) => (
            <motion.div
              key={`k-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card 
                className="glass cursor-pointer hover:border-mystic-300/30 transition-all"
                onClick={() => setExpandedRitual(expandedRitual === idx ? null : idx)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-mystic-300" />
                      {ritual.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-moon-gold/50 text-moon-gold">
                        <Moon className="w-3 h-3 mr-1" />
                        {ritual.moon_phase}
                      </Badge>
                      {expandedRitual === idx ? (
                        <ChevronUp className="w-5 h-5 text-mystic-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-mystic-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-mystic-200/70 text-sm">{ritual.description}</p>
                </CardHeader>
                
                <AnimatePresence>
                  {expandedRitual === idx && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <CardContent className="pt-4 space-y-4 border-t border-white/10">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-mystic-300 font-medium mb-2">Duration</h5>
                            <p className="text-mystic-200/80 text-sm">{ritual.duration}</p>
                          </div>
                          <div>
                            <h5 className="text-mystic-300 font-medium mb-2">Materials Needed</h5>
                            <ul className="text-mystic-200/80 text-sm space-y-1">
                              {ritual.materials?.map((m, i) => (
                                <li key={`k-${i}`} className="flex items-center gap-2">
                                  <span className="text-mystic-400">•</span> {m}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        
                        {ritual.herbs_used && (
                          <div>
                            <h5 className="text-mystic-300 font-medium mb-2">Herbs Used</h5>
                            <div className="flex flex-wrap gap-2">
                              {ritual.herbs_used.map((h, i) => (
                                <Badge key={`k-${i}`} className="bg-green-500/20 text-green-300">
                                  <Leaf className="w-3 h-3 mr-1" /> {h}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <h5 className="text-mystic-300 font-medium mb-2">Steps</h5>
                          <ol className="space-y-2">
                            {ritual.steps?.map((step, i) => (
                              <li key={`k-${i}`} className="text-mystic-200/80 text-sm flex items-start gap-3">
                                <span className="bg-mystic-600/50 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                                  {i + 1}
                                </span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>

                        {ritual.caution && (
                          <div className="glass-light rounded-lg p-3 border-l-4 border-amber-500">
                            <p className="text-amber-200 text-sm">
                              <AlertCircle className="w-4 h-4 inline mr-2" />
                              {ritual.caution}
                            </p>
                          </div>
                        )}

                        <div className="glass-light rounded-lg p-4 text-center">
                          <p className="text-mystic-300 italic">"{ritual.affirmation}"</p>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))}
        </TabsContent>

        {/* Herbs Tab */}
        <TabsContent value="herbs" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {data?.healing_herbs?.map((herb, idx) => (
              <motion.div
                key={`k-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card 
                  className="glass cursor-pointer hover:border-green-500/30 transition-all h-full"
                  onClick={() => setExpandedHerb(expandedHerb === idx ? null : idx)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2 text-lg">
                        <Leaf className="w-5 h-5 text-green-400" />
                        {herb.name}
                      </CardTitle>
                      {expandedHerb === idx ? (
                        <ChevronUp className="w-5 h-5 text-mystic-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-mystic-400" />
                      )}
                    </div>
                    <p className="text-mystic-400 text-xs italic">{herb.latin_name}</p>
                  </CardHeader>
                  
                  <CardContent className="pt-2">
                    <div className="flex flex-wrap gap-1 mb-3">
                      {herb.properties?.map((prop, i) => (
                        <Badge key={`k-${i}`} variant="outline" className="text-xs border-green-500/30 text-green-300">
                          {prop}
                        </Badge>
                      ))}
                    </div>
                    
                    <AnimatePresence>
                      {expandedHerb === idx && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-3 pt-3 border-t border-white/10"
                        >
                          <div>
                            <h5 className="text-mystic-300 font-medium text-sm mb-1">Benefits</h5>
                            <p className="text-mystic-200/80 text-sm">{herb.benefits}</p>
                          </div>
                          <div>
                            <h5 className="text-mystic-300 font-medium text-sm mb-1">How to Use</h5>
                            <div className="flex flex-wrap gap-2">
                              {herb.uses?.map((use, i) => (
                                <Badge key={`k-${i}`} className="bg-mystic-600/30 text-mystic-200 text-xs">
                                  {use}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {herb.caution && (
                            <div className="bg-amber-500/10 rounded-lg p-2 text-xs text-amber-200">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              {herb.caution}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* Crystals Tab */}
        <TabsContent value="crystals" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {data?.healing_crystals?.map((crystal, idx) => (
              <motion.div
                key={`k-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card 
                  className="glass cursor-pointer hover:border-purple-500/30 transition-all h-full"
                  onClick={() => setExpandedCrystal(expandedCrystal === idx ? null : idx)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2 text-lg">
                        <Gem className="w-5 h-5 text-purple-400" />
                        {crystal.name}
                      </CardTitle>
                      {expandedCrystal === idx ? (
                        <ChevronUp className="w-5 h-5 text-mystic-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-mystic-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge className="bg-gradient-to-r from-pink-500/30 to-purple-500/30 text-mystic-200 text-xs">
                        {crystal.color}
                      </Badge>
                      <Badge variant="outline" className="border-purple-500/30 text-purple-300 text-xs">
                        {crystal.chakra}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-2">
                    <div className="flex flex-wrap gap-1 mb-3">
                      {crystal.properties?.map((prop, i) => (
                        <Badge key={`k-${i}`} variant="outline" className="text-xs border-mystic-500/30 text-mystic-300">
                          {prop}
                        </Badge>
                      ))}
                    </div>
                    
                    <AnimatePresence>
                      {expandedCrystal === idx && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-3 pt-3 border-t border-white/10"
                        >
                          <div>
                            <h5 className="text-mystic-300 font-medium text-sm mb-1">Healing Aspects</h5>
                            <p className="text-mystic-200/80 text-sm">{crystal.healing_aspects}</p>
                          </div>
                          <div>
                            <h5 className="text-mystic-300 font-medium text-sm mb-1">How to Use</h5>
                            <ul className="space-y-1">
                              {crystal.how_to_use?.map((use, i) => (
                                <li key={`k-${i}`} className="text-mystic-200/80 text-sm flex items-start gap-2">
                                  <span className="text-purple-400 mt-1">•</span>
                                  {use}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="glass-light rounded-lg p-3 text-center">
                            <p className="text-mystic-300 italic text-sm">"{crystal.affirmation}"</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WombHealing;
